import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  where,
  getDocs,
  query,
  collectionGroup,
  orderBy,
  collection,
  getDoc,
  doc,
  getCountFromServer,
  DocumentData,
  Query,
  QueryDocumentSnapshot,
  startAfter,
  limit,
  DocumentSnapshot,
} from "firebase/firestore";
import { db } from "@/helpers/firebase/firebase";
import { Collections } from "@/helpers/utils/collections";
import { eventsMap, getVenues } from "@/helpers/server-actions/events-collection";
import { EventCheckIn, EventItem } from "@/models/event-item";
import { defaultMap } from "@/helpers/utils/utility-functions";
import {
  CTribeDocument,
  CTribeDocumentFolder,
  CTribeDocumentParticipent,
  CTribeDocumentSpace,
} from "@/models/partner-document";
import { getCTribeDocuments } from "@/helpers/server-actions/ctribe-documents-collection";
import { getGetInvolvedForms } from "@/helpers/server-actions/get-involved-controls-collections";
import { GetInvolvedFormCategoryItem } from "@/models/get-involved";
import { FormResponse } from "@/models/form-types";
import { getAdminResponseWindow } from "@/helpers/utils/admin-form-window";
import { Hotel } from "@/models/hotel";
import { Speaker } from "@/models/speaker";
import { Venue } from "@/models/venue";

interface FormResponseData {
  responses: any[];
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
}

interface EventCheckInsData {
  checkIns: EventCheckIn[];
  lastDoc: DocumentSnapshot | null;
}

const hotelBookingsToFormResponse = (doc: DocumentSnapshot) => {
  const docData = doc.data() as any;
  return {
    ...docData,
    documentName: "hotel-booking",
    formId: "hotel-booking",
    hotel: docData["item"]?.["name"],
    ...docData["bookingInfo"],
    startDate:
      new Date(docData["bookingInfo"]?.["startDate"])?.toDateString() ?? "",
    endDate:
      new Date(docData["bookingInfo"]?.["endDate"])?.toDateString() ?? "",
  };
};

const marketingPopupToFormResponse = (doc: DocumentSnapshot) => {
  const docData = doc.data() as any;
  const interests = docData?.["interests"] || [];
  let filteredInterests: string[] = [];

  if (interests.length > 0) {
    const interestKeys = Object.keys(interests[0]);
    filteredInterests = interestKeys.filter(key => interests[0][key] === "True");
  }

  return {
    ...docData,
    documentName: "marketing-site-popup",
    formId: "marketing-site-popup",
    interests: filteredInterests,
  };
};

export const apiSlice = createApi({
  baseQuery: fakeBaseQuery(),
  endpoints: (build) => ({
    getFormResponse: build.query<any[], void>({
      async queryFn() {
        try {
          const startOfYear = new Date(new Date().getFullYear(), 0, 1);

          const responsesQuery = query(
            collectionGroup(db, "form_responses"),
            where("timestamp", ">=", startOfYear),
            where("status", "!=", "NOT_SUBMITTED"),
            orderBy("timestamp", "desc")
          );

          let querySnapshot = await getDocs(responsesQuery);

          const responses: any[] = [];
          querySnapshot.forEach((doc) => {
            responses.push({ ...doc.data(), documentName: doc.id });
          });

          querySnapshot = await getDocs(
            collectionGroup(db, Collections.HOTEL_BOOKINGS)
          );

          querySnapshot.forEach((doc) => {
            responses.push(hotelBookingsToFormResponse(doc));
          });

          querySnapshot = await getDocs(
            collectionGroup(db, Collections.CONTEST_ENTRIES)
          );

          querySnapshot.forEach((doc) => {
            responses.push(marketingPopupToFormResponse(doc));
          });

          return { data: responses };
        } catch (e) {
          return { error: e };
        }
      },
    }),
    getPaginatedFormResponse: build.query<
      FormResponseData,
      {
        rowsPerPage: number;
        lastDoc: DocumentSnapshot | null;
        formId: string;
      }
    >({
      async queryFn({ rowsPerPage, lastDoc, formId }) {
        try {
          const startOfYear = new Date(new Date().getFullYear(), 0, 1);
          let baseQuery: Query | undefined = undefined;
          if (formId === "hotel-booking") {
            baseQuery = query(
              collectionGroup(db, Collections.HOTEL_BOOKINGS),
              where("timestamp", ">=", startOfYear),
              orderBy("timestamp", "desc"),
              limit(rowsPerPage)
            );
          } else if (formId === "marketing-site-popup") {
            baseQuery = query(
              collectionGroup(db, Collections.CONTEST_ENTRIES),
              where("timestamp", ">=", startOfYear),
              orderBy("timestamp", "desc"),
              limit(rowsPerPage)
            );
          } else {
            baseQuery = query(
              collectionGroup(db, Collections.FORM_RESPONSES),
              where("timestamp", ">=", startOfYear),
              where("status", "!=", "NOT_SUBMITTED"),
              where("formId", "==", formId),
              orderBy("timestamp", "desc"),
              limit(rowsPerPage)
            );
          }

          // Add startAfter if lastDoc is present
          const responsesQuery = lastDoc
            ? query(baseQuery, startAfter(lastDoc))
            : baseQuery;

          const querySnapshot = await getDocs(responsesQuery);

          const responses: any[] = [];
          querySnapshot.forEach((doc) => {
            if (formId === "hotel-booking") {
              responses.push(hotelBookingsToFormResponse(doc));
            } else if (formId === "marketing-site-popup") {
              responses.push(marketingPopupToFormResponse(doc));
            } else {
              responses.push({ ...doc.data(), documentName: doc.id });
            }
          });

          const lastVisible =
            querySnapshot.docs.length > 0
              ? querySnapshot.docs[querySnapshot.docs.length - 1]
              : null;

          return { data: { responses, lastVisible } };
        } catch (e) {
          return { error: e };
        }
      },
    }),

    getFormResponseByFormId: build.query<FormResponse[], string>({
      async queryFn(formId: string) {
        console.log("formId", formId);
        try {
          const startOfYear = new Date(new Date().getFullYear(), 0, 1);
          // Cycle-based forms also show every response for their current cycle.
          const responseWindow = getAdminResponseWindow(formId);

          let baseQuery: Query | undefined = undefined;
          if (formId === "hotel-booking") {
            baseQuery = query(
              collectionGroup(db, Collections.HOTEL_BOOKINGS),
              where("timestamp", ">=", startOfYear),
              orderBy("timestamp", "desc")
            );
          } else if (formId === "marketing-site-popup") {
            baseQuery = query(
              collectionGroup(db, Collections.CONTEST_ENTRIES),
              where("timestamp", ">=", startOfYear),
              orderBy("timestamp", "desc")
            );
          } else {
            baseQuery = query(
              collectionGroup(db, "form_responses"),
              where("timestamp", ">=", responseWindow.since),
              where("status", "!=", "NOT_SUBMITTED"),
              where("formId", "==", formId),
              orderBy("timestamp", "desc")
            );
          }

          const querySnapshot = await getDocs(baseQuery);

          const responses: FormResponse[] = [];
          querySnapshot.forEach((doc) => {
            if (formId === "hotel-booking") {
              responses.push(hotelBookingsToFormResponse(doc));
            } else if (formId === "marketing-site-popup") {
              responses.push(marketingPopupToFormResponse(doc));
            } else {
              const data = doc.data();
              const savedAt = typeof data.timestamp?.toDate === "function" ? data.timestamp.toDate() : null;
              if (!responseWindow.include(doc.id, savedAt)) return;
              responses.push({
                ...data,
                documentName: doc.id,
              } as FormResponse);
            }
          });

          return { data: responses };
        } catch (e) {
          return { error: e };
        }
      },
    }),
    getFormResponseCountByFormId: build.query<number, string>({
      async queryFn(formId) {
        try {
          if (formId === "hotel-booking") {
            const snapshot = await getCountFromServer(
              collectionGroup(db, Collections.HOTEL_BOOKINGS)
            );
            return { data: snapshot.data()?.count ?? 0 };
          } else if (formId === "marketing-site-popup") {
            const snapshot = await getCountFromServer(
              collectionGroup(db, Collections.CONTEST_ENTRIES)
            );
            return { data: snapshot.data()?.count ?? 0 };
          }
          const startOfYear = new Date(new Date().getFullYear(), 0, 1);

          const responsesQuery = query(
            collectionGroup(db, "form_responses"),
            where("formId", "==", formId),
            where("timestamp", ">=", startOfYear),
            where("status", "!=", "NOT_SUBMITTED")
          );

          const querySnapshot = await getCountFromServer(responsesQuery);
          const count = querySnapshot.data()?.count ?? 0;

          return { data: count };
        } catch (e) {
          return { error: e };
        }
      },
    }),
    getPartnerEvents: build.query<EventItem[], string[]>({
      async queryFn(partnerEvents: string[]) {
        if (!partnerEvents) return { data: [] };
        try {
          const eventsQuery = query(
            collection(db, Collections.EVENTS),
            where("__name__", "in", partnerEvents),
            orderBy("title", "asc")
          );

          const querySnapshot = await getDocs(eventsQuery);
          if (querySnapshot.empty) {
            return { data: [] };
          }

          return { data: querySnapshot.docs.map(eventsMap) };
        } catch (e) {
          return { error: e };
        }
      },
    }),
    getDocumentSpaces: build.query<CTribeDocumentSpace[], void>({
      async queryFn() {
        try {
          const documentSpaces = query(
            collection(db, Collections.CTRIBE_DOCUMENT_SPACES)
          );

          const querySnapshot = await getDocs(documentSpaces);
          if (querySnapshot.empty) {
            return { data: [] };
          }
          const spaces = querySnapshot.docs.map(
            defaultMap<CTribeDocumentSpace>
          );

          return { data: spaces };
        } catch (e) {
          return { error: e };
        }
      },
    }),
    getSpaceDocuments: build.query<CTribeDocument, string>({
      async queryFn(docId: string) {
        try {
          const docSnapshot = await getDoc(
            doc(db, Collections.CTRIBE_DOCUMENTS, docId)
          );
          if (!docSnapshot.exists()) {
            return { data: {} as CTribeDocument };
          }
          const cTribeDoc = defaultMap<CTribeDocument>(docSnapshot);

          return { data: cTribeDoc };
        } catch (e) {
          return { error: e };
        }
      },
    }),
    getCTribeDocuments: build.query<CTribeDocument[], void>({
      async queryFn() {
        try {
          return { data: await getCTribeDocuments() };
        } catch (e) {
          return { error: e };
        }
      },
    }),
    getGetInvolvedForms: build.query<GetInvolvedFormCategoryItem[], void>({
      async queryFn() {
        try {
          return { data: await getGetInvolvedForms() };
        } catch (e) {
          return { error: e };
        }
      },
    }),
    getPartnerDocumentFolders: build.query<CTribeDocumentFolder[], string>({
      async queryFn(uid: string) {
        try {
          const assignedSpacesQuery = query(
            collectionGroup(db, Collections.SPACE_PARTICIPANTS),
            where("userId", "==", uid)
          );

          const querySnapshot = await getDocs(assignedSpacesQuery);
          if (querySnapshot.empty) {
            return { data: [] };
          }
          const spaceParticipients = querySnapshot.docs.map(
            defaultMap<CTribeDocumentParticipent>
          );

          const folders: CTribeDocumentFolder[] = [];
          for (let i = 0; i < spaceParticipients.length; i++) {
            const spaceParticipient = spaceParticipients[i];
            const foldersQuery = collection(
              db,
              Collections.CTRIBE_DOCUMENT_SPACES,
              spaceParticipient.spaceId,
              Collections.SPACE_FOLDERS
            );

            const foldersSnapshot = await getDocs(foldersQuery);
            if (foldersSnapshot.empty) {
              continue;
            }
            const currentFolder = foldersSnapshot.docs.map(
              defaultMap<CTribeDocumentFolder>
            );

            for (let j = 0; j < currentFolder.length; j++) {
              const folder = currentFolder[j];
              const documentRefs = folder.documentRefs?.filter(
                (docRef) => docRef.isVisible
              );
              if (documentRefs?.length > 0) {
                const q = query(
                  collection(db, Collections.CTRIBE_DOCUMENTS),
                  where(
                    "__name__",
                    "in",
                    documentRefs.map((docRef) => docRef.documentId)
                  )
                );
                const ctribeDocumentRefs = await getDocs(q);
                folder.documents = ctribeDocumentRefs.docs.map((doc) => ({
                  ...defaultMap<CTribeDocument>(doc),
                  isVisible: true,
                }));
              }
            }
            folders.push(...currentFolder);
          }
          return { data: folders.sort((a, b) => a.sequence - b.sequence) };
        } catch (e) {
          return { error: e };
        }
      },
    }),

    // TODO: DELETE THIS AFTER FESTIVAL
    getHotels: build.query<Hotel[], void>({
      async queryFn() {
        try {
          const snapshot = await getDocs(collection(db, Collections.HOTELS));
          const hotels: Hotel[] = [];
          snapshot.forEach((doc) => {
            hotels.push({ ...doc.data(), id: doc.id } as Hotel);
          });

          return { data: hotels };
        } catch (e) {
          return { error: e };
        }
      },
    }),

    getEventCheckInsCount: build.query<number, string>({
      async queryFn(eventId: string) {
        try {
          const eventCheckInsQuery = query(
            collection(db, Collections.EVENTS, eventId, Collections.EVENT_CHECK_INS)
          );
          const querySnapshot = await getCountFromServer(eventCheckInsQuery);
          return { data: querySnapshot.data()?.count ?? 0 };
        } catch (e) {
          return { error: e };
        }
      },
    }),

    getSpeakersByEvent: build.query<Speaker[], string>({
      async queryFn(eventId: string) {
        try {
          const speakersQuery = query(
            collection(db, Collections.SPEAKERS),
            where("events", "array-contains", eventId)
          );
          const categorySnapshot = await getDocs(speakersQuery);
          const data = categorySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Speaker[];
          return { data };
        } catch (e) {
          return { error: e };
        }
      },
    }),

    getEventsBySpeaker: build.query<EventItem[], string>({
      async queryFn(speakerId: string) {
        try {

          const speakerDocRef = doc(db, Collections.SPEAKERS, speakerId);
          const speakerDoc = await getDoc(speakerDocRef);
    
          if (!speakerDoc.exists()) {
            throw new Error("Speaker not found");
          }
    
          const speakerData = speakerDoc.data() as Speaker;
    
          const eventsArray = speakerData.events;
    
          const eventsPromises = eventsArray.map(eventId => getDoc(doc(db, Collections.EVENTS, eventId)));
          const eventsSnapshots = await Promise.all(eventsPromises);
          const data = eventsSnapshots.map(eventDoc => ({
            id: eventDoc.id,
            ...eventDoc.data()
          })) as EventItem[];
    
          return { data };
        } catch (e) {
          return { error: e };
        }
      },
    }),

    getEventVenues: build.query<Venue[], void>({
      async queryFn() {
        try {
         const venues = await getVenues();
         return {data: venues};
        } catch (e) {
          return { error: e };
        }
      },
    }),

    getPaginatedCheckIns: build.query<EventCheckInsData, { eventId: string; rowsPerPage: number; lastDoc: DocumentSnapshot | null }>({
      async queryFn({ eventId, rowsPerPage, lastDoc }) {
        try {
          const baseQuery = query(
            collection(db, Collections.EVENTS, eventId, Collections.EVENT_CHECK_INS),
            orderBy("timestamp", "desc"),
            limit(rowsPerPage)
          );

          const checkInsQuery = lastDoc
            ? query(baseQuery, startAfter(lastDoc))
            : baseQuery;

          const querySnapshot = await getDocs(checkInsQuery);
          return { data: { checkIns: querySnapshot.docs.map((docData) => {
            const doc = docData.data();
            return {
              ...doc,
              timestamp: doc.timestamp.toDate(),
            } as EventCheckIn;
          }), lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] } };
        } catch (e) {
          return { error: e };
        }
      },
    }),
  }),
});

export const {
  useGetFormResponseQuery,
  useLazyGetPaginatedFormResponseQuery,
  useLazyGetFormResponseByFormIdQuery,
  useLazyGetFormResponseCountByFormIdQuery,
  useLazyGetEventCheckInsCountQuery,
  useLazyGetPartnerEventsQuery,
  useGetDocumentSpacesQuery,
  useGetCTribeDocumentsQuery,
  useGetGetInvolvedFormsQuery,
  useLazyGetPartnerDocumentFoldersQuery,
  useGetHotelsQuery,
  useGetSpeakersByEventQuery,
  useGetEventsBySpeakerQuery,
  useLazyGetPaginatedCheckInsQuery,
  useGetEventVenuesQuery
} = apiSlice;
export default apiSlice;
